export type InlineMoveDirection =
  | 'next'
  | 'prev'
  | 'left'
  | 'right'
  | 'up'
  | 'down';

export const INLINE_CELL_SELECTOR = '[data-pt-nav-cell]';

export const isDropdownOpen = (from?: EventTarget | null) => {
  if (from instanceof HTMLElement) {
    const select = from.closest('.ant-select');
    if (select?.classList.contains('ant-select-open')) return true;
  }

  return !!document.querySelector(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  );
};

const isVisible = (element: HTMLElement) => {
  if (element.closest('[hidden], [aria-hidden="true"]')) return false;
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

export const getEditableNavCells = (root: HTMLElement): HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>(INLINE_CELL_SELECTOR)].filter(
    (cell) =>
      isVisible(cell) &&
      !!cell.querySelector(
        'input:not([type="hidden"]), textarea, .ant-select, .ant-input-number',
      ),
  );

export const focusNavCell = (cell: HTMLElement) => {
  const input = cell.querySelector<HTMLElement>(
    'input:not([type="hidden"]), textarea, .ant-select-selector, .ant-input-number-input',
  );
  input?.focus();
};

const uniqueSorted = (values: number[]) =>
  [...new Set(values)].sort((left, right) => left - right);

export const findInlineTarget = (
  cells: HTMLElement[],
  current: HTMLElement,
  direction: InlineMoveDirection,
): HTMLElement => {
  if (!cells.includes(current)) return current;

  const row = Number(current.dataset.ptRow);
  const col = current.dataset.ptCol || '';
  const rowCells = cells.filter((cell) => Number(cell.dataset.ptRow) === row);
  const indexInRow = rowCells.indexOf(current);
  const rows = uniqueSorted(cells.map((cell) => Number(cell.dataset.ptRow)));
  const rowIndex = rows.indexOf(row);

  if (direction === 'next' || direction === 'right') {
    if (indexInRow < rowCells.length - 1) return rowCells[indexInRow + 1];
    if (direction === 'right') return current;
    const nextRow = rows[rowIndex + 1];
    if (nextRow == null) return current;
    return (
      cells.find((cell) => Number(cell.dataset.ptRow) === nextRow) || current
    );
  }

  if (direction === 'prev' || direction === 'left') {
    if (indexInRow > 0) return rowCells[indexInRow - 1];
    if (direction === 'left') return current;
    const previousRow = rows[rowIndex - 1];
    if (previousRow == null) return current;
    const previousRowCells = cells.filter(
      (cell) => Number(cell.dataset.ptRow) === previousRow,
    );
    return previousRowCells[previousRowCells.length - 1] || current;
  }

  const step = direction === 'down' ? 1 : -1;
  for (let cursor = rowIndex + step; cursor >= 0 && cursor < rows.length; ) {
    const candidate = cells.find(
      (cell) =>
        Number(cell.dataset.ptRow) === rows[cursor] &&
        (cell.dataset.ptCol || '') === col,
    );
    if (candidate) return candidate;
    cursor += step;
  }

  return current;
};
